import { useEffect, useRef } from "react";

/**
 * Acquire a Screen Wake Lock and re-acquire on `visibilitychange` (mandatory:
 * the OS releases it when the tab is hidden).
 *
 * On iOS <18.2 the API is missing entirely — the surrounding UI shows a
 * fallback banner instead of erroring here.
 */
export function useWakeLock(active: boolean): { supported: boolean } {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const supportedRef = useRef<boolean>(typeof navigator !== "undefined" && "wakeLock" in navigator);

  useEffect(() => {
    if (!supportedRef.current) return;
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      if (!active || cancelled) return;
      try {
        sentinelRef.current = await navigator.wakeLock.request("screen");
      } catch {
        // ignore — common when tab is hidden at acquire time
      }
    };

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinelRef.current?.release().catch(() => undefined);
      sentinelRef.current = null;
    };
  }, [active]);

  return { supported: supportedRef.current };
}

interface WakeLockSentinel {
  release: () => Promise<void>;
}

declare global {
  interface Navigator {
    wakeLock: {
      request: (type: "screen") => Promise<WakeLockSentinel>;
    };
  }
}
